// ADDITEMDRAFT1 (docs/build-briefs/ADDITEMDRAFT1-add-item-keeps-draft.md). An unsaved Add item or Edit item form is kept
// as a draft in localStorage, so a browser refresh AND an inactivity sign-out do not cost a person what they typed. No
// DOM: the storage and the clock are injected so a plain node:test file drives it (test/ui-item-draft.test.ts).
//
// Rules, each enforced below and by a test:
//   - keyed by tenant, location, person (employee id) and product id (or "new"): one person's draft is never offered to
//     another, nor at another location, nor for another product;
//   - an allowlist: only the fields in FIELD_SPEC can be stored or read, each with a type and a size cap, on write AND on
//     read. Anything else (a PIN, a passcode, a token, a card detail, a key somebody added by hand) is dropped, never kept;
//   - a draft older than 24 hours reads as no draft and is deleted when read;
//   - small: a stored value over MAX_ITEM_DRAFT_CHARS reads as no draft;
//   - cleared by the caller on a successful save and on Discard. It is deliberately NOT cleared on sign-out: the draft
//     outlives the session, which is the point.
// UX108's screenDrafts.ts is a different store (sessionStorage, cleared on sign-out, and its word list refuses a field
// called "name"), so this one stands alone.

import type { KeyValueStorage } from "./session.ts";

const KEY_PREFIX = "bms-ui-item-draft:";
export const ITEM_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const MAX_ITEM_DRAFT_CHARS = 20_000;
const FORMAT_VERSION = 1;

/** The three steps of the form (Product, How it comes and pours, Price), by the tab name the form already uses. */
export const ITEM_DRAFT_STEPS = ["drink", "inventory", "calculator"] as const;
export type ItemDraftStep = (typeof ITEM_DRAFT_STEPS)[number];

/** Text fields and their longest allowed value. A null mechanism or an unset select is stored as "". */
const TEXT_FIELDS = {
  itemKind: 40,
  saleMechanism: 20,
  name: 200,
  category: 120,
  categoryOther: 120,
  unitPrice: 20,
  shelfTier: 120,
  sizeValue: 20,
  sizeUnit: 40,
  size: 80,
  containerType: 60,
  defaultKegSize: 60,
  parLevel: 20,
  barcode: 60,
  startStock: 20,
  abv: 20,
  producer: 200,
  countryRegion: 120,
  description: 2_000,
  kegWaste: 10,
  vendorId: 80,
  vendorCost: 20,
  vendorPackSize: 20,
  vendorSku: 80,
  vendorOrderUnit: 40,
  typedPriceOunces: 10,
  typedPriceText: 20,
  orderAs: 10,
  caseBarcode: 60,
  caseSize: 20,
  calcOzTarget: 10,
  calcBottleTarget: 10,
} as const;

const FLAG_FIELDS = [
  "stopOrdering",
  "hideOnMenu",
  "trackStock",
  "active",
  "saleMechanismTouched",
  "sizeUserSet",
  "containerUserSet",
  "orderUnitTouched",
  "unitPriceTouched",
  "calcOzTouched",
  "calcBottleTouched",
] as const;

export type ItemDraftTextField = keyof typeof TEXT_FIELDS;
export type ItemDraftFlagField = (typeof FLAG_FIELDS)[number];

export interface ItemDraftLocationStock {
  readonly locationId: string;
  readonly par: string;
  readonly stopOrdering: boolean;
}

export type ItemDraftFields = { readonly [K in ItemDraftTextField]: string } & {
  readonly [K in ItemDraftFlagField]: boolean;
} & {
  /** The pour sizes picked (ounces as text). */
  readonly pourOptions: readonly string[];
  /** Par and stop ordering per location, when the form shows the per-location table. */
  readonly locationStock: readonly ItemDraftLocationStock[];
};

export interface ItemDraft {
  readonly step: ItemDraftStep;
  readonly fields: ItemDraftFields;
}

export interface ItemDraftScope {
  readonly tenantId: string;
  /** The location the form is saving to; "none" when the form has none. */
  readonly locationId: string;
  /** The signed-in person's employee id. */
  readonly userId: string;
  /** The product being edited, or undefined for a new item. */
  readonly productId: string | undefined;
}

const ID = /^[A-Za-z0-9_-]{1,64}$/;

export function itemDraftKey(scope: ItemDraftScope): string {
  return `${KEY_PREFIX}${scope.tenantId}:${scope.locationId}:${scope.userId}:${scope.productId ?? "new"}`;
}

function scopeIsValid(scope: ItemDraftScope): boolean {
  return (
    ID.test(scope.tenantId) &&
    ID.test(scope.userId) &&
    (scope.locationId === "none" || ID.test(scope.locationId)) &&
    (scope.productId === undefined || ID.test(scope.productId))
  );
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Reads a draft's content from untrusted data. Anything not on the allowlist, or of the wrong type or size, makes the
 * whole draft invalid (undefined): a draft is restored whole or not at all. */
export function parseItemDraft(raw: unknown): ItemDraft | undefined {
  const record = asRecord(raw);
  if (record === undefined) return undefined;
  const step = ITEM_DRAFT_STEPS.find((candidate) => candidate === record["step"]);
  const fieldsIn = asRecord(record["fields"]);
  if (step === undefined || fieldsIn === undefined) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, max] of Object.entries(TEXT_FIELDS)) {
    const value = fieldsIn[key];
    if (typeof value !== "string" || value.length > max) return undefined;
    out[key] = value;
  }
  for (const key of FLAG_FIELDS) {
    const value = fieldsIn[key];
    if (typeof value !== "boolean") return undefined;
    out[key] = value;
  }
  const pours = fieldsIn["pourOptions"];
  if (!Array.isArray(pours) || pours.length > 30) return undefined;
  if (!pours.every((p) => typeof p === "string" && p.length > 0 && p.length <= 10))
    return undefined;
  out["pourOptions"] = [...(pours as string[])];
  const stock = fieldsIn["locationStock"];
  if (!Array.isArray(stock) || stock.length > 50) return undefined;
  const locationStock: ItemDraftLocationStock[] = [];
  for (const entry of stock) {
    const e = asRecord(entry);
    const locationId = e?.["locationId"];
    const par = e?.["par"];
    const stopOrdering = e?.["stopOrdering"];
    if (
      typeof locationId !== "string" ||
      !ID.test(locationId) ||
      typeof par !== "string" ||
      par.length > 20 ||
      typeof stopOrdering !== "boolean"
    ) {
      return undefined;
    }
    locationStock.push({ locationId, par, stopOrdering });
  }
  out["locationStock"] = locationStock;
  return { step, fields: out as unknown as ItemDraftFields };
}

/** A form's fields compare equal when every kept field matches; the step on its own is not a change worth keeping. */
export function itemDraftFieldsEqual(a: ItemDraft, b: ItemDraft): boolean {
  return JSON.stringify(a.fields) === JSON.stringify(b.fields);
}

/** Cuts every text field to its cap, so a long description never makes a draft unreadable. */
export function clampItemDraft(draft: ItemDraft): ItemDraft {
  const fields: Record<string, unknown> = { ...draft.fields };
  for (const [key, max] of Object.entries(TEXT_FIELDS)) {
    const value = fields[key];
    if (typeof value === "string" && value.length > max) fields[key] = value.slice(0, max);
  }
  return { step: draft.step, fields: fields as unknown as ItemDraftFields };
}

/** Writes the draft. Returns false (and writes nothing) when the scope, the content or the size is not acceptable, or the
 * storage refuses. Never throws. */
export function saveItemDraft(
  storage: KeyValueStorage,
  scope: ItemDraftScope,
  draft: ItemDraft,
  now: number,
): boolean {
  if (!scopeIsValid(scope)) return false;
  const clean = parseItemDraft(clampItemDraft(draft));
  if (clean === undefined) return false;
  const text = JSON.stringify({
    v: FORMAT_VERSION,
    savedAt: now,
    step: clean.step,
    fields: clean.fields,
  });
  if (text.length > MAX_ITEM_DRAFT_CHARS) return false;
  try {
    storage.setItem(itemDraftKey(scope), text);
    return true;
  } catch {
    // A full or blocked storage loses the draft, never the form the person is typing in.
    return false;
  }
}

/** The stored draft for this scope, or undefined. A corrupt, foreign, oversize or expired value is deleted. */
export function loadItemDraft(
  storage: KeyValueStorage,
  scope: ItemDraftScope,
  now: number,
): ItemDraft | undefined {
  if (!scopeIsValid(scope)) return undefined;
  const key = itemDraftKey(scope);
  let text: string | null;
  try {
    text = storage.getItem(key);
  } catch {
    return undefined;
  }
  if (text === null) return undefined;
  let draft: ItemDraft | undefined;
  if (text.length <= MAX_ITEM_DRAFT_CHARS) {
    try {
      const parsed = asRecord(JSON.parse(text));
      const savedAt = parsed?.["savedAt"];
      if (
        parsed !== undefined &&
        parsed["v"] === FORMAT_VERSION &&
        typeof savedAt === "number" &&
        Number.isFinite(savedAt) &&
        now - savedAt >= 0 &&
        now - savedAt < ITEM_DRAFT_TTL_MS
      ) {
        draft = parseItemDraft(parsed);
      }
    } catch {
      draft = undefined;
    }
  }
  if (draft === undefined) clearItemDraft(storage, scope);
  return draft;
}

export function clearItemDraft(storage: KeyValueStorage, scope: ItemDraftScope): void {
  if (!scopeIsValid(scope)) return;
  try {
    storage.removeItem(itemDraftKey(scope));
  } catch {
    // Nothing to remove from a storage that will not answer.
  }
}

/** A blank set of fields, for building a snapshot from the form. */
export function emptyItemDraftFields(): ItemDraftFields {
  const base: Record<string, unknown> = {};
  for (const key of Object.keys(TEXT_FIELDS)) base[key] = "";
  for (const key of FLAG_FIELDS) base[key] = false;
  base["pourOptions"] = [];
  base["locationStock"] = [];
  return base as unknown as ItemDraftFields;
}
